import { DealTabs, BUYER, CO_BUYER, VEHICLE_ENTITY } from '@makemydeal/dr-shared-types';
import { SAVE_STATE_VALIDATION_RESULT } from '../actionTypes/saveValidationActionTypes';
import { runSaveValidations, type SaveValidationErrorKeys } from '../utils/saveValidationRunner';
import { clearEntityErrors, setErrorFields } from './dealScreenErrorActionCreators';
import { flattenLeaf, flattenNestedErrorFlags } from '../utils/contractingUtils';
import { buildVppProductErrors, VPP_PRODUCT_ERRORS_KEY } from '../utils/vppProductErrors';
import { getVppProducts } from '../selectors/vehicleProtectionSelectors';
import type { FieldErrors } from '../reducers/dealScreenErrorReducer';
import { isOnSaveValidationFlow } from '../selectors/saveValidationSelectors';

export type ValidateSaveStateOptions = {
    skipValidationKeys?: SaveValidationErrorKeys[];
};

type ErrorFlags = Record<string, boolean | Record<string, boolean>>;
type ErrorFlagsCollection = Record<string, ErrorFlags | null | undefined>;

export const setOrClearErrors = (errors: ErrorFlagsCollection, entity: string, errorKey?: string) => {
    const key = errorKey || entity;
    if (errors[key]) {
        return setErrorFields(entity, errors[key] as Record<string, boolean>, true);
    }
    return clearEntityErrors(entity);
};

/**
 * Writes the save-flow product errors onto PROTECTION under its own namespaced key, replaced wholesale
 * each run. Never clears the whole entity: PROTECTION also holds the contract flow's GAP flags.
 */
export const syncVppProductErrors = (errors: ErrorFlagsCollection) => (dispatch: any, getState: any) => {
    const productErrors = buildVppProductErrors(
        errors['vehicleProtectionProducts'] as Record<string, FieldErrors> | null | undefined,
        getVppProducts(getState())
    );

    dispatch(
        setErrorFields(DealTabs.PROTECTION, { [VPP_PRODUCT_ERRORS_KEY]: productErrors } as unknown as Record<string, boolean>, true)
    );
};

/**
 * Writes the missing-vehicle flag onto VEHICLE_ENTITY under its own field, replaced on every run. Never
 * clears the whole entity: it also holds the contract flow's vehicle field errors.
 */
export const syncMissingVehicleError = (errors: ErrorFlagsCollection) => (dispatch: any) => {
    const hasMissingVehicle = Boolean((errors['vehicle'] as ErrorFlags | undefined)?.['hasVehicle']);

    dispatch(setErrorFields(VEHICLE_ENTITY, { hasVehicle: hasMissingVehicle }, true));
};

export const syncRedDotErrors = (errors: ErrorFlagsCollection) => (dispatch: any, getState: any) => {
    dispatch(setOrClearErrors(errors, BUYER));
    dispatch(setOrClearErrors(errors, CO_BUYER, 'coBuyer'));
    dispatch(setOrClearErrors(errors, DealTabs.ACCESSORIES));
    dispatch(setOrClearErrors(errors, DealTabs.INCENTIVES));
    dispatch(setOrClearErrors({ tradeIns: flattenNestedErrorFlags(errors['tradeIns']) }, DealTabs.TRADE_IN, 'tradeIns'));
    syncVppProductErrors(errors)(dispatch, getState);
    syncMissingVehicleError(errors)(dispatch);
};

export const setSaveStateValidationResult = (isValid: boolean, errors: Record<string, string[]>) => ({
    type: SAVE_STATE_VALIDATION_RESULT,
    payload: { isValid, errors }
});

export const validateSaveState = (options?: ValidateSaveStateOptions) => (dispatch: any, getState: any) => {
    const state = getState();
    const { errors, hasErrors } = runSaveValidations(state, options);
    const errorFlags = flattenLeaf(errors, 'hasError');
    const errorMessages = flattenLeaf(errors, 'errorMessage');

    if (!hasErrors) {
        dispatch(setSaveStateValidationResult(true, {}));
    } else {
        dispatch(setSaveStateValidationResult(false, errorMessages));
    }
    syncRedDotErrors(errorFlags)(dispatch, getState);

    return { errors, errorFlags, errorMessages, hasErrors };
};

export const clearSaveValidation = () => (dispatch: any, getState: any) => {
    dispatch(setSaveStateValidationResult(true, {}));
    syncRedDotErrors({})(dispatch, getState);
};

/**
 * Re-runs save validation only when an attempt has already failed, so it can relax state a real attempt
 * established but never newly block a save.
 */
export const revalidateSaveStateIfNeeded = () => (dispatch: any, getState: any) => {
    if (!isOnSaveValidationFlow(getState())) {
        return;
    }

    return validateSaveState()(dispatch, getState);
};
